import type { Enemy } from "../entities/Enemy.ts";
import { breathe, snap, decay } from "./AnimationUtils.ts";
import { arenaMotionScale } from "../engine/MotionSettings.ts";
import { arenaDensityBias } from "../engine/PerfGovernor.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import { haloGlowScale } from "./DensityGlow.ts";
import { deriveRamp, emissiveFill } from "./EmissiveMaterial.ts";
import { enemyVisualRadius } from "./EnemyVisualScale.ts";
import { ENEMY_SHAPES, buildDiamond, buildDart } from "./EnemyShapes.ts";
import type { EnemyShapeFn } from "./EnemyShapes.ts";
import {
  drawStealthGhost,
  drawStealthRevealed,
  getTransitionProgress,
} from "./StealthRenderer.ts";
import { ELEMENT_COLORS } from "./designTokens.ts";
import { COLORS } from "../core/constants.ts";
import { ENEMY_PALETTE } from "../core/palette.ts";

/** BL-287 — the contact shadow under a body. Pure black so it reads as absence
 *  of light rather than as a coloured smudge; the cache turns it into one blit. */
const SHADOW_INK = "#000000";
/**
 * Sprite radius as a fraction of the body. The cached sprite paints ~1.8× its
 * radius, so 0.78 lands the pool's edge at ~1.40× the body — just inside the
 * 1.45× halo, leaving a visible dark annulus between silhouette and glow.
 *
 * Two earlier values were WRONG and the measurement is why: at 0.50 and 0.62 the
 * pool reached only ~0.9× and ~1.12× the body, so the body itself occluded
 * almost all of it (2 px of visible rim at 0.62). It photographed as no change,
 * and a zoomed eyeball comparison mistook the body's breathe animation for the
 * effect. A shadow you cannot measure is not subtle, it is absent.
 *
 * Width, not offset: a wider pool with a tiny offset reads as ambient contact —
 * the right physics for a top-down board — whereas a larger offset reads as a
 * figurine lifted off the surface, which the owner explicitly ruled out.
 */
const SHADOW_SPREAD = 0.78;
/** Offset along the declared key light (up-left ⇒ shadow down-right), in body
 *  radii. Small on purpose: this is contact, not elevation. */
const SHADOW_OFFSET_X = 0.08;
const SHADOW_OFFSET_Y = 0.14;
/** Well under the tower socket's 0.32: enemies are smaller, far more numerous,
 *  and the shadow must ground them without ever becoming a visible object. */
const SHADOW_ALPHA = 0.26;

/**
 * Renders enemy entities: a per-archetype procedural silhouette (BL-51,
 * dispatched on `enemy.shape`) filled by the shared emissive material
 * (BL-26/27), plus glow, health bar, and DoT indicator. Shape-driven cues:
 * `dart` → ghosted afterimage trail, `block` → shield aura + armour cracks.
 */
export class EnemyRenderer {
  private ctx: CanvasRenderingContext2D;
  private time = 0;
  showAllHPBars = false;

  /** Shield-break edge detection: track which enemies had shields last frame. */
  private shieldState = new WeakMap<Enemy, { ver: number; had: boolean }>();
  /** Active shield-break shatter effects (render-only, bounded). */
  private shieldBreaks: Array<{
    x: number;
    y: number;
    r: number;
    startTime: number;
    angles: number[];
  }> = [];
  /** Active death-pop effects (BL-36, render-only, bounded). Seeded from the
   *  `enemy.killed` event via {@link spawnDeathPop} — the enemy is pool-recycled
   *  the same frame, so we capture its silhouette params as primitives here. */
  private deathPops: Array<{
    x: number;
    y: number;
    r: number;
    color: string;
    shape: string;
    heading: number;
    startTime: number;
  }> = [];
  /** Reusable scratch buffers for hex-cage vertices (avoid per-frame alloc). */
  private readonly vertX = [0, 0, 0, 0, 0, 0];
  private readonly vertY = [0, 0, 0, 0, 0, 0];
  private readonly vertCos = [0, 0, 0, 0, 0, 0];
  private readonly vertSin = [0, 0, 0, 0, 0, 0];
  // BL-265 — reused scratch for the live Overseer hosts (aura → host tether).
  // Persisted + length-reset each frame so the render hot path allocates no
  // per-frame array (the list is tiny, 1-2 typically).
  private readonly auraHostsScratch: Enemy[] = [];

  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
  }

  render(enemies: Enemy[], time?: number): void {
    const ctx = this.ctx;
    this.time = time ?? performance.now() * 0.001;

    // BL-53: live on-screen count → density-aware glow damping. A render-only
    // read of the array (nothing feeds the sim); the per-enemy halo recedes
    // under crowding so the additive glows stop merging into a wash, weighted
    // by threat tier so the boss/elite stay dominant. Below DENSITY_SOFT this
    // is a no-op (haloGlowScale → 1) and the sparse frame is byte-identical.
    // BL-265 — one pass computes BOTH the active count (density glow damping)
    // and the live Overseer hosts (aura-buff → host TETHER, S156/PT-41), into
    // a reused scratch array. Previously two separate full scans + a per-frame
    // array allocation. Render-only; the aura list is consulted only when a
    // buffed enemy is drawn.
    let activeCount = 0;
    const auraHosts = this.auraHostsScratch;
    auraHosts.length = 0;
    for (const e of enemies) {
      if (!e.active) continue;
      activeCount++;
      if (e.special?.type === "overseer") auraHosts.push(e);
    }
    // BL-44 rung 3 — under measured load the governor adds "virtual crowding"
    // so the halos recede sooner (a deep, budget-gated cut). 0 when not under
    // load → the haloGlowScale call is byte-identical to BL-53.
    const haloCount = activeCount + arenaDensityBias();

    for (const enemy of enemies) {
      if (!enemy.active) continue;

      const glitch =
        enemy.hp / enemy.maxHp < 0.3 ? (Math.random() - 0.5) * 3 : 0;
      const ex = enemy.x + glitch;
      const ey = enemy.y;
      const breatheScale =
        enemy.slowMultiplier < 1
          ? 1.0
          : 1 + breathe(this.time + enemy.x * 0.01, 0.8) * 0.06;
      // BL-285 — the authored radius is a tier signal, not a draw size. Every
      // body dimension below (silhouette, halo, cues, bars) derives from the
      // PRESENCE radius so the whole enemy scales as one object.
      const vr = enemyVisualRadius(enemy.radius);
      const r = vr * breatheScale;

      // CR-19 / BL-49: Stealth rendering — cloak ↔ white-hot reveal Prunkstück.
      // `transition` is the 0..1 phase-in (−1 when settled); each draw plays its
      // own direction's phase-in (reveal = white-hot, re-cloak = ease back in).
      if (enemy.stealth) {
        const transition = getTransitionProgress(enemy, this.time);

        if (enemy.revealed) {
          // BL-287 — a REVEALED stealth body is not cloaked, so it is grounded
          // like every other body. It is in fact the body that needs it most:
          // `StealthRenderer` gives it the largest halo on the board
          // (`rr * 2.0` against the standard 1.45), so leaving it out made the
          // one silhouette with the biggest glow field the only one with nothing
          // underneath it. Drawn BEFORE the phantom, matching the standard path
          // where the shadow sits under the body and over the ground.
          // The CLOAKED arm below deliberately gets none: a shadow is painted on
          // the board, so it would hand the player exactly the position the
          // cloak exists to withhold.
          glowCache.draw(
            ctx,
            SHADOW_INK,
            r * SHADOW_SPREAD,
            ex + r * SHADOW_OFFSET_X,
            ey + r * SHADOW_OFFSET_Y,
            SHADOW_ALPHA,
          );
          drawStealthRevealed(ctx, enemy, ex, ey, r, this.time, transition);
        } else {
          // BL-88: the caller computes the stealth-hidden flag (= the
          // StealthGuard predicate `enemy.stealth && !enemy.revealed`) and
          // injects it, so `rendering` imports no `systems` value. In this
          // branch the enemy is stealth + not revealed, i.e. hidden.
          const hidden = enemy.stealth && !enemy.revealed;
          drawStealthGhost(ctx, enemy, ex, ey, r, this.time, transition, hidden);
        }

        // Stealth enemies handle their own body draw — skip standard body.
        // F-19: an unrevealed, undamaged stealth enemy must not leak its
        // position via the HP bar (showAllHPBars would otherwise force it on).
        // Once revealed or damaged, the position is already known.
        if (enemy.revealed || enemy.hp < enemy.maxHp) {
          this.drawHealthbar(enemy);
        }
        if (enemy.marked) this.drawMarkIndicator(enemy);
        if (enemy.slowMultiplier < 1) this.drawSlowEffect(enemy, ex, ey, r);
        if (enemy.dots.size > 0) {
          ctx.strokeStyle = enemy.dots.has("virus")
            ? ELEMENT_COLORS.virus
            : ELEMENT_COLORS.fire;
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.arc(enemy.x, enemy.y, vr + 4, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        // Shield bubble on stealth enemies (drawn before continue)
        this.tickShieldState(enemy, ex, ey, r);
        if (enemy.shieldHp > 0) this.drawShield(enemy, ex, ey, r);
        continue;
      }

      // Per-enemy silhouette (BL-51) = lit volume (BL-26/27): the archetype's
      // body path, dispatched on enemy.shape, filled by the emissive ramp
      // derived from its threat colour. Stealth bodies are handled above and
      // untouched. Directional families orient to the travel heading.
      const heading = this.getHeading(enemy);
      const buildBody = ENEMY_SHAPES[enemy.shape] ?? buildDiamond;

      // ── Reaction modulation (BL-36, render-only) — WRAP the S80 silhouette +
      // S79 emissive without changing either: a spawn scale-in (snap) + intro
      // glow, and on hit a squash/twitch (snap) + additive white flash (decay).
      // All damped by motionScale (BL-45): reduced motion → no scale/squash/
      // twitch, the local flash stays (a readability tell) but softened.
      // BL-44: arenaMotionScale() is continuous ∈ [0,1] (accessibility hard-kill
      // at 0; otherwise the governor's gradual load factor). Each reaction
      // magnitude lerps by `m`, byte-identical at m=1 (full) and m=0 (reduced).
      const m = arenaMotionScale();
      const hf = enemy.hitFlashT;
      const sp = enemy.spawnT;
      // Spawn: grow from a seed size with a snap overshoot, eased toward
      // "instant" as motion damps (1 - 0.65·m·(1-snap) ≡ old 0.35+0.65·snap @ m=1).
      const spawnK = sp < 1 ? 1 - 0.65 * m * (1 - snap(sp)) : 1;
      const introGlow = sp < 1 ? (1 - sp) * 0.5 * m : 0;
      const rr = r * spawnK;

      // Outer glow via sprite (brighter during the spawn intro), damped by
      // live density × inverse threat tier (BL-53). At sparse counts gScale is
      // exactly 1 → radius/alpha are the S84 values byte-for-byte; under
      // crowding a trash halo shrinks + dims while the boss/elite keep theirs,
      // so the apex pops out of a receding field instead of a uniform wash.
      // BL-285: 1.6 → 1.45. The bodies grew; if the halo kept its old multiple
      // the additive field would grow WITH them and a late wave would read as a
      // brighter wash rather than as bigger, more legible threats. Absolute
      // halo reach still grows a little (bigger enemies do glow more) — just
      // slower than the silhouettes, which is what keeps the frame calm.
      const gScale = haloGlowScale(enemy, haloCount);
      const haloR = gScale >= 1 ? rr * 1.45 : rr * 1.45 * (0.6 + 0.4 * gScale);
      glowCache.draw(ctx, enemy.color, haloR, ex, ey, (0.3 + introGlow) * gScale);

      // ── Contact shadow (BL-287) ──────────────────────────────────────────
      // The grammar `TowerRenderer.drawSocket` already uses to ground a tower,
      // finally applied to the bodies that move. It is the half of depth an
      // additive halo cannot supply: a halo only ever ADDS light, so twenty
      // glowing bodies over a flat plane merge into a wash instead of reading as
      // a formation. Occlusion is what says "this sits ON the board".
      //
      // Two deliberate restraints, both from the owner's brief ("verankert, ohne
      // wie physische Figuren auf einem Brett auszusehen"):
      //   · the offset is tiny — a big offset reads as a figurine lifted off the
      //     surface, which is a different (and wrong) game;
      //   · it follows the key light the renderer actually declares (up-left, per
      //     `EmissiveMaterial`'s hot-core offset), so down-RIGHT. It does NOT copy
      //     the tower socket's straight-down nudge, which predates that key and
      //     disagrees with it.
      // Drawn AFTER the halo, not before: the halo is painted source-over with a
      // soft coloured sprite, so a shadow underneath it simply gets re-lit and
      // measures as no change. On top, it darkens the halo's inner region and the
      // body then covers the middle — leaving a dark annulus between silhouette
      // and glow, which is what actually reads as "seated". One cached blit. Position,
      // not motion: no reduced-motion variant, and deliberately NOT density-damped
      // — the shadow is exactly what keeps a crowded field legible.
      // Placed after the stealth branch on purpose: a cloaked enemy must never be
      // given away by its own shadow.
      // Reachable by the degrade ladder, and by the repo's OWN doctrine rather
      // than an ad-hoc gate: the pool shrinks on the same `gScale` the halo uses
      // (BL-53), so under measured crowding a trash shadow recedes while the
      // boss/elite keep theirs. Dropping shadows outright under load was the
      // other option and is the wrong one — the shadow is what keeps a crowded
      // field legible, so removing it exactly when the field crowds trades away
      // the thing it was added for. Shrinking reclaims the fill (area falls with
      // the square) and keeps the apex grounded.
      const shadowR =
        gScale >= 1
          ? rr * SHADOW_SPREAD
          : rr * SHADOW_SPREAD * (0.55 + 0.45 * gScale);
      glowCache.draw(
        ctx,
        SHADOW_INK,
        shadowR,
        ex + rr * SHADOW_OFFSET_X,
        ey + rr * SHADOW_OFFSET_Y,
        SHADOW_ALPHA,
      );

      // Hit squash + backward twitch along the travel heading. The amplitude `k`
      // scales by `m`, so the squash damps gradually under load (= full @ m=1).
      const squashing = hf > 0 && m > 0;
      if (squashing) {
        const k = snap(hf > 1 ? 1 : hf) * m;
        const tw = 1.6 * k;
        ctx.save();
        ctx.translate(-Math.cos(heading) * tw, -Math.sin(heading) * tw);
        ctx.translate(ex, ey);
        ctx.scale(1 + 0.2 * k, 1 - 0.14 * k);
        ctx.translate(-ex, -ey);
      }

      const ramp = deriveRamp(enemy.color);
      ctx.fillStyle = emissiveFill(ctx, ex, ey, rr, ramp);
      ctx.beginPath();
      buildBody(ctx, ex, ey, rr, this.time, heading);
      ctx.fill();

      // Inner edge — the same silhouette rebuilt at 0.6 scale (for the diamond
      // this reproduces the original inner outline exactly).
      ctx.strokeStyle = "rgba(255,255,255,0.25)";
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      buildBody(ctx, ex, ey, rr * 0.6, this.time, heading);
      ctx.stroke();

      // Scanline (generic surface detail, shape-agnostic)
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(ex - rr * 0.5, ey);
      ctx.lineTo(ex + rr * 0.5, ey);
      ctx.stroke();

      // ── Hit feedback (BL-285) ────────────────────────────────────────────
      // The old tell was an additive pure-white fill of the WHOLE silhouette at up
      // to α0.85: every hit blanked the body to a white blob, so the archetype
      // the player was trying to read — its colour, its material, its shape —
      // disappeared for exactly the frames they were looking hardest at it. In a
      // sustained beam that is most of the enemy's life on screen.
      //
      // What replaces it never touches white and never covers the body:
      //   (a) an EMISSIVE LIFT in the enemy's own `ramp.mid`, added over itself
      //       — luminance and saturation climb, the hue does not move, so it
      //       reads as the thing getting hot rather than being erased;
      //   (b) a RIM PULSE tracing the silhouette outline, so the frame the eye
      //       catches carries the SHAPE — the archetype gets more legible on a
      //       hit, not less.
      //
      // There WAS a third channel — a contact ring discharging just off the rim
      // — and it was cut on measurement (BL-287). Difference renders over five
      // archetypes put the rim pulse at 5,586 px / mean Δ61 and the ring at
      // 5,110 px / mean Δ53: near-identical footprint AND intensity, i.e. two
      // thin bright curves doing one job at the same instant. The rim survives
      // because it traces the REAL silhouette — on a dart, worm or cluster that
      // is a different curve from a circle, and shape legibility is the whole
      // point of this pass. Cutting the ring also removes the one element that
      // repeated at weapon cadence, and takes a hit frame to 2 silhouette builds
      // where it started at 4.
      // Both fall off on `decay` (spent within ~2 frames) and stay legible under
      // reduced motion / load: `m` only trims magnitude.
      if (hf > 0) {
        const prevOp = ctx.globalCompositeOperation;
        ctx.globalCompositeOperation = "lighter";
        const k = 1 - decay(1 - hf);
        if (k > 0.01) {
          // (a) and (b) are the SAME path at the same radius, so they are built
          // once and both painted from it. Measured first rather than guessed:
          // dropping (a) outright was the obvious "simplify", but a difference
          // render showed it moving 12.7k px at a mean delta of 28 — it is what
          // makes the body read as getting HOT, not a redundant third effect. So
          // the overload is paid off in path builds instead: a hit frame now
          // costs 3 silhouette builds instead of 4, with zero visual change.
          // That matters most on the expensive builders (the overseer alone is
          // 9 lobes + 5 tentacles + a nucleus) under sustained fire.
          ctx.beginPath();
          buildBody(ctx, ex, ey, rr, this.time, heading);
          ctx.globalAlpha = (0.20 + 0.14 * m) * k;
          ctx.fillStyle = ramp.mid;
          ctx.fill();
          ctx.globalAlpha = (0.45 + 0.3 * m) * k;
          ctx.strokeStyle = ramp.core;
          ctx.lineWidth = 1.6;
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = prevOp;
      }

      if (squashing) ctx.restore();

      this.drawHealthbar(enemy);

      // Mark indicator (SIGNAL tower debuff)
      if (enemy.marked) {
        this.drawMarkIndicator(enemy);
      }

      // Slow/Freeze visual (CR-4)
      if (enemy.slowMultiplier < 1) {
        this.drawSlowEffect(enemy, ex, ey, r);
      }

      // DoT indicator
      if (enemy.dots.size > 0) {
        ctx.strokeStyle = enemy.dots.has("virus")
          ? ELEMENT_COLORS.virus
          : ELEMENT_COLORS.fire;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.arc(enemy.x, enemy.y, enemy.radius + 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Shape-specific visual cues
      this.drawTypeCue(enemy, ex, ey, r, heading);

      // Shield bubble (S60) — after type cue, on top of the enemy
      this.tickShieldState(enemy, ex, ey, r);
      if (enemy.shieldHp > 0) this.drawShield(enemy, ex, ey, r);

      // S154 — Overseer aura buff halo (the "protected — focus the host" tell).
      // S156 — + a tether to the host so the player SEES which Overseer to kill.
      if (enemy.auraBuffTimer > 0) {
        this.drawAuraBuff(ex, ey, r);
        this.drawAuraLink(ex, ey, auraHosts);
      }
    }

    // Draw any active shield-break shatter effects
    this.renderShieldBreaks();
    // Draw any active death-pops (kills that despawned this/last frames)
    this.renderDeathPops();
  }

  /** Seed a death-pop from a killed enemy (BL-36). Called from the `enemy.killed`
   *  event (wired in main.ts via EntityRenderer) BEFORE the pool recycles the
   *  slot, so the silhouette params are still valid; we copy them as primitives.
   *  The additive particle burst is `emitKill` (combatHandlers) — untouched. */
  spawnDeathPop(enemy: Enemy): void {
    // A still-hidden stealth enemy must not reveal its position post-mortem.
    if (enemy.stealth && !enemy.revealed) return;
    if (this.deathPops.length >= 24) this.deathPops.shift();
    this.deathPops.push({
      x: enemy.x,
      y: enemy.y,
      r: enemyVisualRadius(enemy.radius),
      color: enemy.color,
      shape: enemy.shape,
      heading: this.getHeading(enemy),
      startTime: this.time,
    });
  }

  /** Render active death-pops: the silhouette scales out (snap) and fades
   *  (decay) additively — a kill reads as an over-bright bloom pop (§6.8), not
   *  an instant vanish. BL-230: the first ~half of the pop also **datamoshes**
   *  (RGB chromatic split + horizontal slice-displacement) so an AI/cyber enemy
   *  reads as *corrupting* before it blooms — the payoff of the Fire→Impact→
   *  Death loop. Damped under reduced motion (no scale, no glitch, softer). */
  private renderDeathPops(): void {
    if (this.deathPops.length === 0) return;
    const ctx = this.ctx;
    const DURATION = 0.18; // 180ms
    // BL-230: the glitch lives in the first GLITCH_FRAC of the pop (~90 ms) and
    // fades out as the clean bloom resolves.
    const GLITCH_FRAC = 0.5;
    const m = arenaMotionScale();
    for (let i = this.deathPops.length - 1; i >= 0; i--) {
      const p = this.deathPops[i]!;
      const elapsed = this.time - p.startTime;
      if (elapsed < 0 || elapsed > DURATION) {
        this.deathPops.splice(i, 1);
        continue;
      }
      const t = elapsed / DURATION; // 0..1
      // Pop scale + brightness ease with motion (≡ old m===0 branches at 0/1).
      const popScale = 1 + 0.7 * m * snap(t < 1 ? t : 1);
      const alpha = (0.6 + 0.4 * m) * (1 - decay(t));
      if (alpha <= 0.01) continue;
      const rr = p.r * popScale;
      const build = ENEMY_SHAPES[p.shape] ?? buildDiamond;
      const ramp = deriveRamp(p.color);
      const prevOp = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = "lighter";

      // BL-230: datamosh layer — motion-gated (m===0 under reduced motion → gi
      // is 0 → only the clean pop draws below), deterministic (no RNG), brief.
      const gi = m * Math.max(0, 1 - t / GLITCH_FRAC); // 1 at the kill → 0
      if (gi > 0.01) {
        this.drawDeathGlitch(build, p.x, p.y, rr, p.heading, ramp, alpha, gi);
      }

      // Body — the clean over-bright bloom pop (the resolve the screen acks).
      ctx.globalAlpha = alpha * 0.9;
      ctx.fillStyle = emissiveFill(ctx, p.x, p.y, rr, ramp);
      ctx.beginPath();
      build(ctx, p.x, p.y, rr, this.time, p.heading);
      ctx.fill();
      // Bright core kick.
      ctx.globalAlpha = alpha;
      glowCache.draw(ctx, "#FFFFFF", rr * 0.8, p.x, p.y, alpha * 0.8);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = prevOp;
    }
  }

  /** BL-230 — the "glitch death" overlay drawn UNDER the clean bloom pop: a
   *  brief RGB chromatic split (cyan + rose ghosts offset opposite ways) plus a
   *  3-band **horizontal slice-displacement** of the dying silhouette (datamosh
   *  tear). Reuses the StealthRenderer glitch vocabulary. Caller has already set
   *  `globalCompositeOperation = "lighter"`; `gi` (0..1, kill→resolve) carries
   *  the `arenaMotionScale` factor so this is silent under reduced motion. The
   *  band offsets are deterministic from the kill site + render clock (no RNG)
   *  → render-only, sim byte-identical, photosensitivity-safe (small per-enemy
   *  displacement, not a full-screen luminance flash). */
  private drawDeathGlitch(
    build: EnemyShapeFn,
    px: number,
    py: number,
    rr: number,
    heading: number,
    ramp: ReturnType<typeof deriveRamp>,
    alpha: number,
    gi: number,
  ): void {
    const ctx = this.ctx;
    const time = this.time;
    const seed = px * 0.7 + py * 1.3; // stable per-kill phase

    // ── RGB chromatic split — cyan + rose phantoms pulled apart by `chrom` px ──
    const chrom = 2 + 4 * gi;
    ctx.globalAlpha = alpha * 0.55 * gi;
    ctx.fillStyle = COLORS.ICE_CYAN;
    ctx.beginPath();
    build(ctx, px - chrom, py, rr, time, heading);
    ctx.fill();
    ctx.fillStyle = COLORS.ORACLE_RED;
    ctx.beginPath();
    build(ctx, px + chrom, py, rr, time, heading);
    ctx.fill();

    // ── horizontal slice displacement — 3 bands shifted in X (the datamosh) ──
    const bands = 3;
    for (let b = 0; b < bands; b++) {
      const y0 = py - rr + ((2 * rr) * b) / bands;
      const bh = (2 * rr) / bands;
      const off = Math.sin(seed + b * 2.7 + time * 33) * rr * 0.6 * gi;
      ctx.save();
      ctx.beginPath();
      ctx.rect(px - rr * 2, y0, rr * 4, bh);
      ctx.clip();
      ctx.globalAlpha = alpha * (0.5 + 0.4 * gi);
      ctx.fillStyle = emissiveFill(ctx, px + off, py, rr, ramp);
      ctx.beginPath();
      build(ctx, px + off, py, rr, time, heading);
      ctx.fill();
      ctx.restore();
    }
  }

  /**
   * Shape-driven visual cues. Pre-BL-51 these keyed off `enemy.type`, but
   * `type = config.name` ("Data Packet", "Rootkit", …) so the `"fast"`/`"tank"`
   * branches never fired — dead code. They now dispatch on the archetype
   * `shape`, which is what actually varies: dart = fast packet, block = tank.
   */
  private drawTypeCue(
    enemy: Enemy,
    ex: number,
    ey: number,
    r: number,
    heading: number,
  ): void {
    if (enemy.shape === "dart") {
      this.drawSpeedTrail(enemy, ex, ey, r, heading);
    } else if (enemy.shape === "block") {
      this.drawArmorCue(enemy, ex, ey, r);
    } else if (enemy.shape === "jammer") {
      this.drawJammerField(enemy, ex, ey);
    } else if (enemy.shape === "overseer") {
      this.drawOverseerAura(enemy, ex, ey);
    } else if (enemy.shape === "leech") {
      this.drawLeechSiphon(ex, ey, r);
    } else if (enemy.shape === "regenerator") {
      this.drawRegenPulse(enemy, ex, ey, r);
    } else if (enemy.shape === "splitter") {
      this.drawSplitterFracture(enemy, ex, ey, r);
    } else if (enemy.shape === "ram") {
      this.drawBreachCue(enemy, ex, ey, r, heading);
    } else if (enemy.shape === "glyph") {
      this.drawChainWard(enemy, ex, ey, r);
    }
  }

  /** S158 — the LEECH siphon: amber "credit" rings COLLAPSING inward (it pulls
   *  your money in), plus a gorged amber core glow. Continuous while it lives so
   *  the drain reads in-lane (the owner's #1); the HUD `−N` confirms the cost. */
  private drawLeechSiphon(ex: number, ey: number, r: number): void {
    const ctx = this.ctx;
    const m = arenaMotionScale();
    ctx.save();
    ctx.strokeStyle = COLORS.AMBER_WARN;
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 3; i++) {
      const t = (this.time * 0.8 * (0.4 + 0.6 * m) + i / 3) % 1; // inward phase
      ctx.globalAlpha = (1 - t) * 0.5;
      ctx.beginPath();
      ctx.arc(ex, ey, r * (2.4 - t * 1.8), 0, Math.PI * 2); // 2.4r → 0.6r
      ctx.stroke();
    }
    glowCache.draw(
      ctx,
      COLORS.AMBER_WARN,
      r * 0.8,
      ex,
      ey,
      0.25 + breathe(this.time, 2) * 0.15,
    );
    ctx.restore();
  }

  /** S158 — the REGENERATOR heal: mint rings EXPANDING out + a heal "+" cross
   *  (the universal regen tell), shown only while it's BELOW max HP (actively
   *  knitting). Mint contrasts its rose body so "it's healing — out-burst it"
   *  reads instantly. Reduced-motion safe. */
  private drawRegenPulse(enemy: Enemy, ex: number, ey: number, r: number): void {
    if (enemy.hp >= enemy.maxHp) return; // topped off → no pulse
    const ctx = this.ctx;
    const m = arenaMotionScale();
    ctx.save();
    ctx.strokeStyle = COLORS.SENTINEL_GREEN;
    ctx.lineWidth = 1.4;
    for (let i = 0; i < 2; i++) {
      const t = (this.time * 1.1 * (0.4 + 0.6 * m) + i / 2) % 1;
      ctx.globalAlpha = (1 - t) * 0.5;
      ctx.beginPath();
      ctx.arc(ex, ey, r * (0.8 + t * 1.4), 0, Math.PI * 2);
      ctx.stroke();
    }
    glowCache.draw(
      ctx,
      COLORS.SENTINEL_GREEN,
      r * 0.6,
      ex,
      ey,
      0.3 + breathe(this.time, 3) * 0.2,
    );
    const cs = r * 0.5;
    ctx.globalAlpha = 0.7;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(ex - cs, ey);
    ctx.lineTo(ex + cs, ey);
    ctx.moveTo(ex, ey - cs);
    ctx.lineTo(ex, ey + cs);
    ctx.stroke();
    ctx.restore();
  }

  /** S158 — the SPLITTER fracture: a cross-crack across the body that BRIGHTENS +
   *  flickers as HP drops, telegraphing the imminent break into four (the death
   *  spawns the fast Splinters — the visible burst). Render-only. */
  private drawSplitterFracture(
    enemy: Enemy,
    ex: number,
    ey: number,
    r: number,
  ): void {
    const sev = 1 - enemy.hp / enemy.maxHp; // brighter as it nears the split
    if (sev < 0.02) return;
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const flick = 0.5 + Math.abs(Math.sin(this.time * 8 + enemy.x * 0.05)) * 0.5;
    ctx.save();
    ctx.strokeStyle = enemy.color;
    ctx.globalAlpha = Math.min(0.85, 0.3 + sev * 0.6) * flick;
    ctx.lineWidth = 1 + sev;
    const f = r * (1.1 + sev * 0.3 * m);
    ctx.beginPath();
    ctx.moveTo(ex - f, ey);
    ctx.lineTo(ex + f, ey);
    ctx.moveTo(ex, ey - f);
    ctx.lineTo(ex, ey + f);
    ctx.stroke();
    ctx.restore();
  }

  /** S163 / BL-167 — the FIREWALL BREAKER (`ram`) in-grid breach cue: a cracking
   *  SENTINEL-green firewall arc + hot spark shards bursting off the ram's LEADING
   *  (contact) edge, so its wall-eat reads in the lane (it previously had only a
   *  card cue). Echoes the EnemyHolo `drawBreachCardCue` palette — green = YOUR
   *  firewall being breached — for card↔token parity. The renderer holds no
   *  firewall list (render stays decoupled from the sim), so the cue is a
   *  continuous leading-edge tell like the other archetype cues, not proximity-
   *  gated; the spark percussion supplies the "ramming" read. Render-only;
   *  reduced-motion safe (the pulse + shard length scale by motion). */
  private drawBreachCue(
    enemy: Enemy,
    ex: number,
    ey: number,
    r: number,
    heading: number,
  ): void {
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const impact =
      0.5 + Math.abs(Math.sin(this.time * 4 + enemy.x * 0.05)) * 0.5;
    // The contact edge: just ahead of the nose along the travel heading.
    const nx = ex + Math.cos(heading) * r * 1.1;
    const ny = ey + Math.sin(heading) * r * 1.1;
    ctx.save();
    // The breached firewall: a green crack arc ahead of the nose.
    ctx.strokeStyle = COLORS.SENTINEL_GREEN;
    ctx.lineWidth = 1.4;
    ctx.globalAlpha = 0.26 + impact * 0.3 * (0.4 + 0.6 * m);
    ctx.beginPath();
    ctx.arc(ex, ey, r * 1.5, heading - 0.6, heading + 0.6);
    ctx.stroke();
    // Hot impact flare + spark shards bursting off the contact edge.
    glowCache.draw(
      ctx,
      COLORS.SENTINEL_GREEN,
      r * 0.5,
      nx,
      ny,
      (0.2 + impact * 0.25) * (0.5 + 0.5 * m),
    );
    ctx.globalCompositeOperation = "lighter";
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const a = heading + (i - 1) * 0.5;
      const d0 = r * 1.15;
      const d1 = r * (1.3 + impact * 0.5 * m);
      ctx.globalAlpha = (0.24 + impact * 0.34) * (0.4 + 0.6 * m);
      ctx.beginPath();
      ctx.moveTo(ex + Math.cos(a) * d0, ey + Math.sin(a) * d0);
      ctx.lineTo(ex + Math.cos(a) * d1, ey + Math.sin(a) * d1);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** S163 / BL-167 — the CIPHER BREAKER (`glyph`) chain-immunity ward: a faint
   *  lightning-tinted hull ring plus a brief glint that GLANCES off the body, so
   *  "why didn't my chain hit it?" reads in the lane (the Cipher carries
   *  `immunities: ["chain"]`). The glint is the chain element's own colour
   *  deflecting at the hull — a quick shimmer, not a constant hum. glyph is the
   *  Cipher's 1:1 silhouette ↔ mechanic family. Render-only; reduced-motion safe
   *  (the faint ward ring holds at m=0; the glint + skate scale by motion). */
  private drawChainWard(enemy: Enemy, ex: number, ey: number, r: number): void {
    const ctx = this.ctx;
    const m = arenaMotionScale();
    // A sharpened pulse so the deflect reads as an occasional glint, not a hum.
    const beat = 0.5 + 0.5 * Math.sin(this.time * 3 + enemy.x * 0.05);
    const glint = beat * beat * beat; // ∈ [0,1], spends most of the cycle dim
    const skate = this.time * 0.6 * m + enemy.x * 0.02;
    ctx.save();
    ctx.strokeStyle = ELEMENT_COLORS.lightning;
    // The ward hull: a faint full ring — the always-on "shielded" read.
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.16 + 0.1 * m;
    ctx.beginPath();
    ctx.arc(ex, ey, r * 1.22, 0, Math.PI * 2);
    ctx.stroke();
    // The deflect glint: a bright short arc skating around the hull.
    ctx.globalCompositeOperation = "lighter";
    ctx.lineWidth = 1.6;
    ctx.globalAlpha = (0.2 + glint * 0.6) * (0.4 + 0.6 * m);
    ctx.beginPath();
    ctx.arc(ex, ey, r * 1.28, skate, skate + 0.9);
    ctx.stroke();
    // A spark tick at the leading end of the glint (the chain snapping away).
    const sa = skate + 0.9;
    glowCache.draw(
      ctx,
      ELEMENT_COLORS.lightning,
      r * 0.3,
      ex + Math.cos(sa) * r * 1.28,
      ey + Math.sin(sa) * r * 1.28,
      glint * 0.5 * (0.4 + 0.6 * m),
    );
    ctx.restore();
  }

  /** S154 — the Jammer's suppression field: a pulsing dashed magenta ring at its
   *  `jamRadius`, so the player SEES the zone where towers run sluggish (and can
   *  spread out of it). Render-only; reduced-motion safe (pulse + spin × motion). */
  private drawJammerField(enemy: Enemy, ex: number, ey: number): void {
    const radius = enemy.special?.jamRadius;
    if (!radius) return;
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const ringR = radius * (1 + breathe(this.time, 1.4) * 0.05 * m);
    const col = enemy.color || ENEMY_PALETTE.jammer!;
    ctx.save();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.22 + breathe(this.time, 1.4) * 0.12 * (0.4 + 0.6 * m);
    ctx.setLineDash([5, 7]);
    ctx.lineDashOffset = -this.time * 16 * m; // slow sweep when motion on
    ctx.beginPath();
    ctx.arc(ex, ey, ringR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  /** S154 — the Overseer's force-multiplier aura: a pulsing solid violet ring at
   *  its `auraRadius` so the buffed pack reads clearly — kill the host to collapse
   *  it. Render-only; reduced-motion safe. */
  private drawOverseerAura(enemy: Enemy, ex: number, ey: number): void {
    const radius = enemy.special?.auraRadius;
    if (!radius) return;
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const ringR = radius * (1 + breathe(this.time, 0.9) * 0.04 * m);
    const col = enemy.color || ENEMY_PALETTE.overseer!;
    ctx.save();
    glowCache.draw(ctx, col, ringR * 0.5, ex, ey, 0.1 + 0.06 * m);
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.6;
    ctx.globalAlpha = 0.28 + breathe(this.time, 0.9) * 0.14 * (0.4 + 0.6 * m);
    ctx.beginPath();
    ctx.arc(ex, ey, ringR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** S154 — a buffed enemy (inside a living Overseer's aura) wears a thin violet
   *  halo so "this one is protected — focus the host" reads at a glance. */
  private drawAuraBuff(ex: number, ey: number, r: number): void {
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const col = ENEMY_PALETTE.overseer!;
    ctx.save();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.4;
    ctx.globalAlpha = 0.35 + breathe(this.time, 1.6) * 0.18 * (0.4 + 0.6 * m);
    ctx.beginPath();
    ctx.arc(ex, ey, r + 3.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  /** S156 / PT-41 — the aura TETHER: a faint, sweeping violet line from a buffed
   *  enemy to its (nearest in-range) Overseer host, so the player SEES which host
   *  to focus to collapse the buff. Render-only; reduced-motion safe (the dash
   *  sweep + part of the alpha scale by motion; at m=0 a static faint tether
   *  still shows the link). */
  private drawAuraLink(ex: number, ey: number, hosts: Enemy[]): void {
    if (hosts.length === 0) return;
    let best: Enemy | null = null;
    let bestD2 = Infinity;
    for (const h of hosts) {
      const radius = h.special?.auraRadius ?? 0;
      const dx = h.x - ex;
      const dy = h.y - ey;
      const d2 = dx * dx + dy * dy;
      if (d2 <= radius * radius && d2 < bestD2) {
        bestD2 = d2;
        best = h;
      }
    }
    if (!best) return;
    const ctx = this.ctx;
    const m = arenaMotionScale();
    ctx.save();
    ctx.strokeStyle = ENEMY_PALETTE.overseer!;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.16 + breathe(this.time, 1.2) * 0.12 * (0.4 + 0.6 * m);
    ctx.setLineDash([2, 4]);
    ctx.lineDashOffset = -this.time * 12 * m;
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(best.x, best.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  /** dart (Data Packet): fading afterimages of the dart silhouette trailing
   *  behind it along the travel direction — a motion-blur "fast" read. */
  private drawSpeedTrail(
    enemy: Enemy,
    ex: number,
    ey: number,
    r: number,
    heading: number,
  ): void {
    const ctx = this.ctx;
    const backward = this.getFastTrailVector(enemy);
    const ghosts = 3;
    ctx.fillStyle = enemy.color;
    for (let i = 1; i <= ghosts; i++) {
      const f = i / ghosts;
      const ox = ex + backward.x * (r * 0.45 + i * 4.2);
      const oy = ey + backward.y * (r * 0.45 + i * 4.2);
      const gr = r * (0.8 - f * 0.16);
      ctx.globalAlpha = Math.max(0.08, 0.28 - f * 0.09);
      ctx.beginPath();
      buildDart(ctx, ox, oy, gr, this.time, heading);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /** block (Rootkit, tank): cyan armour aura + HP-driven shell cracks layered
   *  over the heavy hexagon. (The wave-15 shield bubble, if any, draws on top.) */
  private drawArmorCue(enemy: Enemy, ex: number, ey: number, r: number): void {
    const ctx = this.ctx;
    const hpRatio = enemy.hp / enemy.maxHp;
    const crackSeverity = 1 - hpRatio;
    // Shield glow ring via sprite
    glowCache.draw(
      ctx,
      COLORS.ICE_CYAN,
      r + 3,
      ex,
      ey,
      0.3 + breathe(this.time, 1.2) * 0.2 + crackSeverity * 0.16,
    );
    ctx.strokeStyle = COLORS.ICE_CYAN;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha =
      0.35 + breathe(this.time, 1.2) * 0.2 + crackSeverity * 0.16;
    ctx.beginPath();
    ctx.arc(ex, ey, r + 3, 0, Math.PI * 2);
    ctx.stroke();

    if (hpRatio < 0.66) {
      const crackCount = hpRatio < 0.33 ? 4 : 2;
      const flicker =
        0.35 + Math.abs(Math.sin(this.time * 12 + enemy.x * 0.04)) * 0.5;
      ctx.strokeStyle = "#9BEAFF";
      ctx.lineWidth = 1;
      ctx.globalAlpha = Math.min(0.85, 0.35 + crackSeverity * 0.5) * flicker;
      for (let i = 0; i < crackCount; i++) {
        const a = this.time * 0.4 + (i / crackCount) * Math.PI * 2;
        const innerR = r + 1 + i * 0.25;
        const outerR = r + 5 + (hpRatio < 0.33 ? i * 0.6 : 0);
        const kink = 0.35 + i * 0.1;
        const mx = ex + Math.cos(a + kink) * ((innerR + outerR) * 0.5);
        const my = ey + Math.sin(a + kink) * ((innerR + outerR) * 0.5);

        ctx.beginPath();
        ctx.moveTo(ex + Math.cos(a) * innerR, ey + Math.sin(a) * innerR);
        ctx.lineTo(mx, my);
        ctx.lineTo(
          ex + Math.cos(a + kink * 1.55) * outerR,
          ey + Math.sin(a + kink * 1.55) * outerR,
        );
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }

  /** Forward travel direction in radians (0 = +x). Derived from the same
   *  path vector the afterimage trail uses; defaults to facing +x when idle. */
  private getHeading(enemy: Enemy): number {
    const b = this.getFastTrailVector(enemy); // backward unit vector
    return Math.atan2(-b.y, -b.x);
  }

  private getFastTrailVector(enemy: Enemy): { x: number; y: number } {
    const path = enemy.path;
    if (!path || path.length === 0) return { x: -1, y: 0 };

    const targetIndex = Math.min(Math.max(enemy.pathIndex, 0), path.length - 1);
    const target = path[targetIndex];
    if (!target) return { x: -1, y: 0 };

    let dx = enemy.x - target.x;
    let dy = enemy.y - target.y;
    let len = Math.hypot(dx, dy);

    if (len < 0.001 && targetIndex > 0) {
      const prev = path[targetIndex - 1];
      if (prev) {
        dx = prev.x - target.x;
        dy = prev.y - target.y;
        len = Math.hypot(dx, dy);
      }
    }

    if (len < 0.001) return { x: -1, y: 0 };
    return { x: dx / len, y: dy / len };
  }

  private drawMarkIndicator(enemy: Enemy): void {
    const ctx = this.ctx;
    const x = enemy.x;
    const y = enemy.y - enemyVisualRadius(enemy.radius) - 12;
    const s = 4;
    glowCache.draw(ctx, ELEMENT_COLORS.signal, 6, x, y, 0.6);
    ctx.fillStyle = ELEMENT_COLORS.signal;
    // Diamond crosshair
    ctx.beginPath();
    ctx.moveTo(x, y - s);
    ctx.lineTo(x + s, y);
    ctx.lineTo(x, y + s);
    ctx.lineTo(x - s, y);
    ctx.closePath();
    ctx.fill();
  }

  /** CR-4: Ice crystal lines rotating around slowed enemies */
  private drawSlowEffect(
    enemy: Enemy,
    ex: number,
    ey: number,
    r: number,
  ): void {
    const ctx = this.ctx;
    const intensity = 1 - enemy.slowMultiplier; // 0..1
    const rotation = this.time * 2;
    ctx.strokeStyle = COLORS.ICE_CYAN;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.4 + intensity * 0.4;
    for (let i = 0; i < 4; i++) {
      const angle = rotation + (i / 4) * Math.PI * 2;
      const innerR = r + 2;
      const outerR = r + 2 + intensity * 6;
      ctx.beginPath();
      ctx.moveTo(ex + Math.cos(angle) * innerR, ey + Math.sin(angle) * innerR);
      ctx.lineTo(ex + Math.cos(angle) * outerR, ey + Math.sin(angle) * outerR);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawHealthbar(enemy: Enemy): void {
    const ctx = this.ctx;
    const ratio = enemy.hp / enemy.maxHp;

    // Only show HP bar if damaged or showAllHPBars is enabled
    if (ratio >= 1 && !this.showAllHPBars) return;

    // BL-285 — the bar tracks the PRESENCE radius, so it keeps clearing the
    // (now larger) body instead of being swallowed by it.
    const vr = enemyVisualRadius(enemy.radius);
    const barWidth = vr * 2.5;
    const barHeight = 2;
    const x = enemy.x - barWidth / 2;
    const y = enemy.y - vr - 7;

    ctx.fillStyle = "#111";
    ctx.fillRect(x - 1, y - 1, barWidth + 2, barHeight + 2);

    const hpColor =
      ratio > 0.5
        ? COLORS.SENTINEL_GREEN
        : ratio > 0.25
          ? "#ffaa00"
          : "#ff2222";
    ctx.fillStyle = hpColor;
    ctx.fillRect(x, y, barWidth * ratio, barHeight);

    // Shield bar: thin azure bar above the HP bar (only while shield is active)
    if (enemy.shieldHp > 0 && enemy.shieldMax > 0) {
      const shieldRatio = enemy.shieldHp / enemy.shieldMax;
      const sy = y - 3;
      ctx.fillStyle = "#111";
      ctx.fillRect(x - 1, sy - 1, barWidth + 2, 3);
      ctx.fillStyle = "#5B8CFF";
      ctx.fillRect(x, sy, barWidth * shieldRatio, 2);
    }
  }

  /** Track shield state for break-flash edge detection. */
  private tickShieldState(
    enemy: Enemy,
    ex: number,
    ey: number,
    r: number,
  ): void {
    const prev = this.shieldState.get(enemy);
    const hadShield = prev?.ver === enemy.version && prev.had;
    const hasShield = enemy.shieldHp > 0;
    // Mutate the existing record in place rather than allocating a new object
    // every frame — this runs for every active enemy on every frame.
    if (prev === undefined) {
      this.shieldState.set(enemy, { ver: enemy.version, had: hasShield });
    } else {
      prev.ver = enemy.version;
      prev.had = hasShield;
    }

    // Detect the shield-breaking frame
    if (hadShield && !hasShield) {
      this.triggerShieldBreak(ex, ey, r);
    }
  }

  /** Spawn a shield-break shatter burst (render-only). */
  private triggerShieldBreak(x: number, y: number, r: number): void {
    const angles: number[] = [];
    for (let i = 0; i < 6; i++) {
      angles.push((i / 6) * Math.PI * 2 - Math.PI / 6);
    }
    // Cap active breaks to prevent runaway (8 max)
    if (this.shieldBreaks.length >= 8) this.shieldBreaks.shift();
    this.shieldBreaks.push({ x, y, r, startTime: this.time, angles });
  }

  /** Render active shield-break shard + ring effects. */
  private renderShieldBreaks(): void {
    const ctx = this.ctx;
    const DURATION = 0.28; // 280ms
    const RING_DURATION = 0.22;

    for (let i = this.shieldBreaks.length - 1; i >= 0; i--) {
      const brk = this.shieldBreaks[i]!;
      const elapsed = this.time - brk.startTime;
      if (elapsed > DURATION) {
        this.shieldBreaks.splice(i, 1);
        continue;
      }

      const t = elapsed / DURATION; // 0..1

      // Hex shards flying outward
      for (const angle of brk.angles) {
        const dist = brk.r * 0.5 + t * brk.r * 4 * (DURATION / 0.28);
        const sx = brk.x + Math.cos(angle + t * 0.3) * dist;
        const sy = brk.y + Math.sin(angle + t * 0.3) * dist;
        const len = brk.r * 0.5 * (1 - t);
        const alpha = 1 - t;
        ctx.strokeStyle = "#CFE0FF";
        ctx.lineWidth = 2 * (1 - t);
        ctx.globalAlpha = alpha * 0.9;
        ctx.beginPath();
        ctx.moveTo(
          sx - Math.cos(angle) * len * 0.5,
          sy - Math.sin(angle) * len * 0.5,
        );
        ctx.lineTo(
          sx + Math.cos(angle) * len * 0.5,
          sy + Math.sin(angle) * len * 0.5,
        );
        ctx.stroke();
      }

      // Shock ring
      if (elapsed < RING_DURATION) {
        const rt = elapsed / RING_DURATION;
        const ringR = brk.r * 1.32 + rt * brk.r * 1.4;
        ctx.strokeStyle = "#5B8CFF";
        ctx.lineWidth = 2.5 * (1 - rt);
        ctx.globalAlpha = (1 - rt) * 0.8;
        const prevOp = ctx.globalCompositeOperation;
        ctx.globalCompositeOperation = "lighter";
        ctx.beginPath();
        ctx.arc(brk.x, brk.y, ringR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalCompositeOperation = prevOp;
      }

      // Core pop flash
      if (elapsed < RING_DURATION) {
        const ft = elapsed / RING_DURATION;
        ctx.globalAlpha = (0.9 - ft * 0.9) * 0.7;
        glowCache.draw(
          ctx,
          "#CFE0FF",
          brk.r * 1.3,
          brk.x,
          brk.y,
          ctx.globalAlpha,
        );
      }

      ctx.globalAlpha = 1;
    }
  }

  /** Draw the faceted hex-cage shield bubble around an enemy. */
  private drawShield(enemy: Enemy, ex: number, ey: number, r: number): void {
    const ctx = this.ctx;
    const ratio = enemy.shieldHp / enemy.shieldMax;
    const rot = this.time * 0.5;
    const Rs = r * 1.32; // outer hex radius

    // Background glow
    glowCache.draw(
      ctx,
      "#5B8CFF",
      Rs * (0.85 + ratio * 0.15),
      ex,
      ey,
      0.12 + ratio * 0.3,
    );

    const cageAlpha = 0.25 + ratio * 0.55 + breathe(this.time, 0.9) * 0.12;
    const segs = Math.ceil(ratio * 6);

    // Compute hex vertices into reusable scratch buffers (no per-frame alloc).
    // cos/sin are cached so the inner rim can reuse them without recomputing.
    const vx = this.vertX;
    const vy = this.vertY;
    const vc = this.vertCos;
    const vs = this.vertSin;
    for (let i = 0; i < 6; i++) {
      const angle = rot + (i * Math.PI) / 3 - Math.PI / 6;
      const c = Math.cos(angle);
      const s = Math.sin(angle);
      vc[i] = c;
      vs[i] = s;
      vx[i] = ex + c * Rs;
      vy[i] = ey + s * Rs;
    }

    // Outer hex cage — draw only `segs` chords (depletion effect)
    ctx.strokeStyle = "#5B8CFF";
    ctx.lineWidth = 0.8 + ratio * 0.8;
    ctx.globalAlpha = cageAlpha;
    const inset = (1 - ratio) * 0.18; // endpoint inset as shield depletes
    for (let i = 0; i < segs; i++) {
      const j = (i + 1) % 6;
      const x0 = vx[i]!;
      const y0 = vy[i]!;
      const dx = vx[j]! - x0;
      const dy = vy[j]! - y0;
      ctx.beginPath();
      ctx.moveTo(x0 + dx * inset, y0 + dy * inset);
      ctx.lineTo(vx[j]! - dx * inset, vy[j]! - dy * inset);
      ctx.stroke();
    }

    // Inner plate rim (only while ratio > 0.4)
    if (ratio > 0.4 && r >= 7) {
      const innerR = r * 1.12;
      ctx.strokeStyle = "#5B8CFF";
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const px = ex + vc[i]! * innerR;
        const py = ey + vs[i]! * innerR;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    }

    // Vertex "bolts" — tiny filled diamonds at each drawn vertex
    if (r >= 7) {
      ctx.fillStyle = "#CFE0FF";
      const bs = 1.6;
      for (let i = 0; i < segs; i++) {
        const x = vx[i]!;
        const y = vy[i]!;
        ctx.beginPath();
        ctx.moveTo(x, y - bs);
        ctx.lineTo(x + bs, y);
        ctx.lineTo(x, y + bs);
        ctx.lineTo(x - bs, y);
        ctx.closePath();
        ctx.fill();
      }
    }

    ctx.globalAlpha = 1;
  }
}
